import { API_CONFIG } from '../config';
import { lookupLabel, normalizeTypeKey } from './mandatoryDocumentTypes';

function toDateOnly(value) {
  if (value == null || value === '') return null;
  const str = String(value);
  const iso = str.match(/^(\d{4}-\d{2}-\d{2})/);
  if (iso) return iso[1];
  const dt = new Date(value);
  return Number.isNaN(dt.getTime()) ? null : dt.toISOString().slice(0, 10);
}

/** Resolve /uploads/... paths against the API host (not the CS Admin SPA origin). */
export function resolveCompanyDocumentFileUrl(fileUrl) {
  if (!fileUrl) return null;
  const apiRoot = (API_CONFIG.getCurrentApiUrl?.() || API_CONFIG.BASE_URL || '')
    .replace(/\/api\/v1\/?$/, '');
  const origin = apiRoot || (typeof window !== 'undefined' ? window.location.origin : '');

  if (/^https?:\/\//i.test(fileUrl)) {
    try {
      const parsed = new URL(fileUrl);
      if (parsed.pathname.startsWith('/uploads/')) {
        return `${origin}${parsed.pathname}${parsed.search}`;
      }
    } catch (_) {
      // fall through
    }
    return fileUrl;
  }

  return `${origin}${fileUrl.startsWith('/') ? fileUrl : `/${fileUrl}`}`;
}

export function mapCompanyApiDocument(doc, lookupItems, idx = 0) {
  const rawType = doc.doc_type || doc.category || doc.doc_category || '';
  const typeCode = normalizeTypeKey(rawType);
  const typeLabel = lookupLabel(lookupItems, typeCode)
    || doc.document_name
    || doc.name
    || 'Document';
  const documentId = doc.document_id ?? doc.id ?? null;

  return {
    id: documentId || `row-${idx}`,
    documentId,
    source: doc.source || 'company',
    typeCode: typeCode || null,
    typeLabel,
    type: typeLabel,
    title: doc.document_name || doc.name || typeLabel,
    fileName: doc.fileName || doc.document_name || doc.name || 'document',
    fileUrl: doc.fileUrl || doc.path || doc.document_path || null,
    mimeType: doc.mimeType || doc.mime_type || null,
    fileSize: doc.fileSize ?? doc.file_size ?? null,
    issueDate: toDateOnly(doc.issueDate ?? doc.issue_date),
    expiryDate: toDateOnly(doc.expiryDate ?? doc.expiry_date),
    uploadDate: doc.uploadDate || doc.upload_date || doc.created_at || null,
    uploadedBy: doc.uploadedBy || doc.uploaded_by || null,
    number: doc.documentNumber ?? doc.doc_number ?? null,
    notes: doc.notes ?? null,
  };
}

/** Shape expected by DocumentViewerModal (also accepts director-style fields). */
export function toDocumentViewerModel(doc, lookupItems) {
  if (!doc) return null;
  const typeCode = doc.typeCode || doc.documentType || doc.type;
  
  // Resolve fileUrl with fallbacks
  const rawFileUrl = doc.fileUrl || doc.path || doc.document_path;
  const resolvedFileUrl = resolveCompanyDocumentFileUrl(rawFileUrl);
  
  // Debug logging for missing file URLs in development
  if (!resolvedFileUrl && process.env.NODE_ENV === 'development') {
    console.warn('toDocumentViewerModel: Missing fileUrl after resolution', {
      doc,
      rawFileUrl,
      resolvedFileUrl
    });
  }
  
  return {
    ...doc,
    source: doc.source || 'company',
    documentType: typeCode,
    documentTypeLabel: doc.typeLabel || lookupLabel(lookupItems, typeCode) || typeCode,
    documentNumber: doc.documentNumber ?? doc.number ?? '',
    fileUrl: resolvedFileUrl,
    fileType: doc.mimeType || doc.fileType || doc.mime_type || null,
    uploadedDate: doc.uploadedDate || doc.uploadDate || null,
    uploadedBy: doc.uploadedBy || 'N/A',
  };
}
